'use strict';

/**
 * Pict-Provider-FeatureBoardAPI
 * -----------------------------
 * Browser-side client for the Feature board and the acceptance decision.
 *
 * Features get a board of their own because work items move at agent speed and features move at
 * product speed. One surface implied they should move together, and that implication is the
 * pressure that produces rubber stamps.
 *
 * AppData.FeatureBoard = { Lanes, CanAccept, Loading, Error }
 *   card = { IDFeature, FeatureNumber, Name, Status, Priority, AcceptanceSentence,
 *            WorkItemsConnected, WorkItemsDone, WorkItemsOpen, LastLandedDate,
 *            Commits, AsBuiltsDeployed, AsBuiltsValidated,
 *            StillFailingReview, PriorAcceptances }
 *
 * The counts ride on the card on purpose. A card that showed only a title would leave the reader
 * with nothing to judge but the title, which is how a queue becomes a stack of rubber stamps.
 */

const libPictProvider = require('pict').ServiceProviderBase;
const libFrameworkClient = require('./FrameworkClient.js');

const _DefaultProviderConfiguration =
{
	ProviderIdentifier: 'FeatureBoardAPI',
	Dependencies: [],
	RoutePrefix: '/1.0/'
};

const _SORT_STEP = 100; // sparse SortOrder so a drop rarely renumbers a lane

class PictProviderFeatureBoardAPI extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, Object.assign({}, _DefaultProviderConfiguration, pOptions || {}), pServiceHash);
		this.routePrefix = this.options.RoutePrefix;
	}

	store()
	{
		if (!this.fable.AppData.FeatureBoard)
		{
			this.fable.AppData.FeatureBoard = { Lanes: [], CanAccept: false, Loading: false, Error: null };
		}
		return this.fable.AppData.FeatureBoard;
	}

	_url(pPath) { let tmpPrefix = this.routePrefix; if (tmpPrefix.charAt(tmpPrefix.length - 1) !== '/') { tmpPrefix += '/'; } return tmpPrefix + pPath.replace(/^\//, ''); }

	// The whole board in one read, mirrored into AppData.FeatureBoard.
	list()
	{
		let tmpStore = this.store();
		tmpStore.Loading = true;
		return libFrameworkClient.getJSON(this.fable, this._url('FeatureBoard'))
			.then((pBody) =>
			{
				tmpStore.Lanes = (pBody && Array.isArray(pBody.Lanes)) ? pBody.Lanes : [];
				tmpStore.CanAccept = !!(pBody && pBody.CanAccept);
				tmpStore.Loading = false;
				tmpStore.Error = null;
				return tmpStore.Lanes;
			})
			.catch((pError) =>
			{
				tmpStore.Loading = false;
				tmpStore.Error = (pError && pError.message) || String(pError);
				return [];
			});
	}

	// The evidence one decision is made against: the acceptance sentence, the counts, the work
	// items themselves, and every prior decision on this feature.
	acceptance(pIDFeature)
	{
		return libFrameworkClient.getJSON(this.fable, this._url('Feature/' + encodeURIComponent(pIDFeature) + '/Acceptance'));
	}

	/**
	 * Record the decision. pOutcome is 'Accept' | 'SendBack' and pNote is the markdown, which the
	 * server requires either way: it 400s an empty note rather than accepting a blank verdict.
	 *
	 * pCloseWorkItems is the set of still-open work items the person ticked in the confirm list, to
	 * be marked Done along with the feature. The server ignores it on a send-back and drops any id
	 * that does not belong to this feature.
	 */
	decide(pIDFeature, pOutcome, pNote, pCloseWorkItems)
	{
		let tmpBody = { Outcome: pOutcome, Note: pNote || '' };
		if (Array.isArray(pCloseWorkItems) && pCloseWorkItems.length) { tmpBody.CloseWorkItems = pCloseWorkItems; }
		return libFrameworkClient.postJSON(this.fable, this._url('Feature/' + encodeURIComponent(pIDFeature) + '/Accept'), tmpBody);
	}

	/**
	 * One decision about a named set of features.
	 *
	 * For the case where a person has already made one judgement that covers many -- most obviously
	 * the backfill, where features finished long before there was a column to record an acceptance
	 * in. The server records BatchSize on every row, so the history says this was one judgement
	 * about N and never claims N separate readings.
	 */
	decideMany(pIDFeatures, pOutcome, pNote, pCloseWorkItems)
	{
		return libFrameworkClient.postJSON(this.fable, this._url('Feature/Accept/Bulk'),
			{
				IDFeatures: pIDFeatures || [], Outcome: pOutcome,
				Note: pNote || '', CloseWorkItems: !!pCloseWorkItems
			});
	}

	nextSortOrder(pLaneKey)
	{
		let tmpMax = 0;
		(this.store().Lanes || []).forEach((pLane) =>
		{
			if (pLane.Key !== pLaneKey) { return; }
			(pLane.Cards || []).forEach((pCard) => { if (pCard.SortOrder > tmpMax) { tmpMax = pCard.SortOrder; } });
		});
		return tmpMax + _SORT_STEP;
	}

	/**
	 * Drag a feature to another lane. The generic entity PUT, exactly as the work item board does
	 * it, so the server audit logs the transition.
	 *
	 * Every lane, Done included: moving a card to Done prospectively is ordinary practice and is not
	 * refused. What a plain write cannot do is stamp AcceptedDate, so a feature dropped on Done is
	 * finished with no assessment recorded, and says so on its card until somebody writes one. The
	 * view opens the acceptance dialog right after this drop lands, as an invitation rather than a
	 * toll gate.
	 */
	moveCard(pIDFeature, pToStatus, pSortOrder)
	{
		return libFrameworkClient.updateEntity(this.fable, 'Feature',
			{ IDFeature: pIDFeature, Status: pToStatus, WorkflowStatus: pToStatus, SortOrder: pSortOrder });
	}
}

module.exports = PictProviderFeatureBoardAPI;
module.exports.default_configuration = _DefaultProviderConfiguration;
